import { USER_ROLE_VALUES } from '@cult/domain';
import { z } from 'zod';
import { PublicUserSchema } from './auth';
import {
  OffsetQuerySchema,
  type SortOrder,
  SortOrderSchema,
  coercedBoolField,
  createdAtField,
  qField,
  timeZoneField,
} from './common';

// ── Auth: Allowed domains ──────────────────────────────────────────────────

export const AdminDomainCreateInputSchema = z.object({
  domain: z
    .string()
    .min(1)
    .max(253)
    .regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z]{2,})+$/, 'Invalid domain format')
    .transform((d) => d.toLowerCase().trim()),
});
export type AdminDomainCreateInput = z.infer<typeof AdminDomainCreateInputSchema>;

export const AdminDomainSchema = z.object({
  id: z.string().uuid(),
  domain: z.string(),
  createdBy: z.string().uuid().nullable(),
  createdAt: createdAtField,
});
export type AdminDomain = z.infer<typeof AdminDomainSchema>;

export const AdminDomainListResponseSchema = z.object({
  domains: z.array(AdminDomainSchema),
});
export type AdminDomainListResponse = z.infer<typeof AdminDomainListResponseSchema>;

// ── Auth: Email invites ────────────────────────────────────────────────────

export const AdminInviteCreateInputSchema = z.object({
  email: z
    .string()
    .email()
    .max(254)
    .transform((e) => e.toLowerCase().trim()),
});
export type AdminInviteCreateInput = z.infer<typeof AdminInviteCreateInputSchema>;

export const AdminInviteSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  invitedBy: z.string().uuid().nullable(),
  createdAt: createdAtField,
  usedAt: z.string().datetime().nullable(),
});
export type AdminInvite = z.infer<typeof AdminInviteSchema>;

export const AdminInviteListResponseSchema = z.object({
  invites: z.array(AdminInviteSchema),
});
export type AdminInviteListResponse = z.infer<typeof AdminInviteListResponseSchema>;

export const AdminUserSchema = PublicUserSchema.extend({
  isActive: z.boolean(),
  createdAt: createdAtField,
  // Last time the user had the site open (to within 5 minutes); null when never seen since tracking began.
  lastSeenAt: z.string().nullable(),
  // Lifetime points balance. Present only when the list is requested with `includeBalance=true`.
  balance: z.number().int().optional(),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;

export const AdminUserListResponseSchema = z.object({
  users: z.array(AdminUserSchema),
  total: z.number().int(),
});
export type AdminUserListResponse = z.infer<typeof AdminUserListResponseSchema>;

export const USER_SORTS = {
  JOINED: 'joined',
  BALANCE: 'balance',
  LAST_SEEN: 'lastSeen',
} as const;
export type UserSort = (typeof USER_SORTS)[keyof typeof USER_SORTS];
export const USER_SORT_VALUES = Object.values(USER_SORTS) as [UserSort, ...UserSort[]];
export const UserSortSchema = z.enum(USER_SORT_VALUES);

/** @alias SortOrder — asc/desc is not user-specific */
export type UserOrder = SortOrder;

export const AdminUserListQuerySchema = OffsetQuerySchema.extend({
  // Accept one role (?role=admin) or many (?role=admin&role=moderator); always normalized to an array.
  role: z
    .union([z.enum(USER_ROLE_VALUES), z.array(z.enum(USER_ROLE_VALUES))])
    .transform((v) => (Array.isArray(v) ? v : [v]))
    .optional(),
  isActive: coercedBoolField,
  q: qField,
  // Opt-in: when true, each user is returned with their lifetime points `balance`.
  includeBalance: coercedBoolField,
  sort: UserSortSchema.optional(),
  order: SortOrderSchema.optional(),
});
export type AdminUserListQuery = z.infer<typeof AdminUserListQuerySchema>;

// Same filters and sort as the list; every matching row, no pagination or balances.
export const AdminUserExportQuerySchema = AdminUserListQuerySchema.omit({
  limit: true,
  offset: true,
  includeBalance: true,
}).extend({
  tz: timeZoneField,
});
export type AdminUserExportQuery = z.infer<typeof AdminUserExportQuerySchema>;

export const AdminUserUpdateInputSchema = z
  .object({
    role: z.enum(USER_ROLE_VALUES).optional(),
    isActive: z.boolean().optional(),
  })
  .strict()
  .refine((v) => v.role !== undefined || v.isActive !== undefined, {
    message: 'At least one field is required',
  });
export type AdminUserUpdateInput = z.infer<typeof AdminUserUpdateInputSchema>;
